import React from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';

import APIConfig from 'config/api';
import { CompanyType } from 'constants/company';
import { EMAIL_MESSAGE_TYPE } from 'constants/email';
import { EVENT } from 'constants/event';
import {
  companyListsCategoryItem,
  GLOBAL_SUPPLIER_LISTS_PERMISSION_GROUP,
  GlobalSupplierListCategoryItemType,
  GlobalSupplierListPermissionGroupType,
  isGlobalSupplierListCategoryItem,
  LIST_CATEGORY_ID,
  ListCategoryIdType,
  ListCategoryMap,
  ListCategoryMapItemType,
  myListsCategoryItem,
  otherCompanyListsCategoryItem
} from 'constants/list';
import { PERSON_STATUS_CODE } from 'constants/person';
import { PERMISSION, PermissionType, PRODUCT_TYPE, ProductType, EIPProductIdType } from 'constants/product';
import { SupplierVendorMappingCollection } from 'constants/supplier';
import { createRequestErrorMessage } from 'util/error';
import { noop } from 'util/function';
import {
  areAllAllowedByPermissionGroups,
  convertGrantScopeMapToPermissionGroups,
  GlobalSupplierListPermissionGrantScopeMapType,
  GlobalSupplierListType,
  isListActionAllowedByPermissionGroups
} from 'util/globalSupplierList';
import { caseInsensitiveLocaleCompare } from 'util/sort';
import { getRelatedCompanyKeysForSupplierIdOnBidTeam } from 'util/supplier';
import { getBidTeamMemberCompanyKeys, transformToCuratedBidTeams } from 'util/transform/bidTeam';
import {
  getCompanyKeysFromRawSupplierGroups,
  SupplierGroupCompanyPersonType,
  SupplierGroupCompanyType,
  SupplierGroupType,
  transformToCuratedSupplierGroups
} from 'util/transform/supplierGroup';
import {
  getCompanyKeysFromGroupedSupplierList,
  transformSupplierListMembersToSearchResult
} from 'util/transform/supplierList';
import {
  getPersonCountForSupplierRequestsByCompany,
  groupSupplierRequestsByCompany
} from 'util/transform/supplierRequest';
import { useEventBus } from 'hooks/event/useEventBus';
import { useQueryParameters } from 'hooks/url/useQueryParameters';

//
// --- Types ---

// context provider

interface ProductContextProviderProps {
  /** company key for currently logged in user */
  personCompany: CompanyType;
  /** person key for currently logged in user */
  personKey: number;
}

// state

interface ContractProductContextState {
  contractTitle: string;
  supplierTeams: ReadonlyArray<any>;
}

interface DiscoveryProductContextState {
  /** true if companies request running; these are the companies we display in the company select */
  areCompaniesLoading: boolean;
  /** true if list request running; these are the names of lists we display in the list select */
  areListsLoading: boolean;
  /** true if list supplier request running; these are the individual supplier company results we display to users */
  areListSuppliersLoading: boolean;
  /** array of unique companies available in company dropdown */
  companies: ReadonlyArray<any>;
  /** array of companies key for suppliers that are on the currently selected list (`currentSelectedList`) */
  companyKeysOnCurrentSelectedList: ReadonlyArray<number>;
  /* permission group for creating new lists */
  createSupplierListPermissionGroups: ReadonlyArray<GlobalSupplierListPermissionGroupType>;
  /** array of list objects available for selection in list dropdown */
  currentLists: ReadonlyArray<GlobalSupplierListType>;
  /** list of supplier objects displayed for currently selected list (corresponds to `currentSelectedList`)  */
  currentListSuppliers: ReadonlyArray<any>;
  /** company key for currently selected company */
  currentSelectedCompanyKey?: number;
  /** list data for list used selected in list dropdown (`currentLists`) */
  currentSelectedList?: GlobalSupplierListType;
  /** currently selected list category (ex: Company Lists or My Lists) */
  currentSelectedListCategoryId: ListCategoryIdType;
  /* permission group for deleting lists */
  deleteSupplierListPermissionGroups: ReadonlyArray<GlobalSupplierListPermissionGroupType>;
  /* permission group for editing lists */
  editSupplierListPermissionGroups: ReadonlyArray<GlobalSupplierListPermissionGroupType>;
  /** true if logged in person can delete list; false otherwise */
  loggedInPersonCanDeleteCurrentSelectedList: boolean;
  /** true if logged in person can edit list; false otherwise */
  loggedInPersonCanEditCurrentSelectedList: boolean;
  /* permission group for viewing lists */
  viewSupplierListPermissionGroups: ReadonlyArray<GlobalSupplierListPermissionGroupType>;
}

interface PortalProductContextState {
  areSupplierGroupsLoading: boolean;
  portalTitle: string;
  supplierGroups: ReadonlyArray<SupplierGroupType>;
  supplierVendorMappings: SupplierVendorMappingCollection;
}

interface SourcingProductContextState {
  areBidTeamsLoading: boolean;
  bidEventTitle: string;
  bidTeams: ReadonlyArray<any>;
  supplierRequestPersonCount: number;
  supplierRequests: ReadonlyArray<any>;
  supplierVendorMappings: SupplierVendorMappingCollection;
}

type ProductContextState = {
  contract: ContractProductContextState;
  discovery: DiscoveryProductContextState;
  isDiscovery?: boolean;
  loggedInPersonCompany: CompanyType;
  loggedInPersonKey: number;
  portal: PortalProductContextState;
  productKey?: EIPProductIdType;
  productType?: ProductType;
  sourcing: SourcingProductContextState;
};

interface InitializeStateOptions {
  personCompany: CompanyType;
  personKey: number;
}

// actions

interface CreateSupplierTeamOptions {
  afterCreate?: () => void;
}

interface CreateBidTeamOptions extends CreateSupplierTeamOptions {
  isDuplicateSelection?: boolean;
  personCompositeKey?: string;
  /** string in the format of "companyKey:personKey" ... not 100% sure what it actually does */
  radioPerson?: string;
  referredBy?: string;
  shouldInvitePerson?: boolean;
}

interface CreateSupplierGroupOptions extends CreateSupplierTeamOptions {
  personKey?: number;
}

interface CreateSupplierGroupCompanyPerson {
  email: string;
  firstName: string;
  lastName: string;
  workPhone?: string;
}

interface CreateSupplierGroupCompanyOptions {
  groupId?: number;
  person?: CreateSupplierGroupCompanyPerson;
}

interface CreateSupplierGroupCompanyPersonOptions {
  groupId?: number;
}

interface CreateSupplierTeamMemberOptions {
  afterCreate?: () => void;
}

interface CreateBidTeamMemberOptions extends CreateBidTeamOptions {
  leadCompanyKey?: number;
}

interface CreateSupplierGroupMemberOptions extends CreateSupplierTeamMemberOptions {}

interface ExecuteGlobalSupplierListsRequestOptions {
  afterExecute?: () => void;
  companyKey?: number;
  newSelectedListKey?: number;
  shouldUpdateSelectedCompanyKey?: boolean;
  shouldUpdateSelectedList?: boolean;
  shouldUpdateSelectedListCategory?: boolean;
}

interface ExecuteGlobalSupplierListSuppliersRequestOptions {
  afterExecute?: () => void;
  globalSupplierList?: GlobalSupplierListType;
}

interface ExecuteRefreshGlobalSupplierListsOptions {
  hasSelectedListBeenRemoved?: boolean;
}

interface ExecuteRefetchPortalSupplierGroupOptions {
  afterExecute?: () => void;
}

interface SaveListOptions {
  isNewList?: boolean;
}

interface SendSupplierCompanyPersonNotificationOptions {
  updatedEmailAddress?: string;
}

interface UpdateSupplierTeamOptions {
  errorMessage?: string;
  successMessage?: string;
}

interface UpdateDiscoverySelectedListOptions {
  afterUpdate?: () => void;
}

interface UpdateDiscoverySelectedListCompanyOptions {
  afterUpdate?: () => void;
}

interface DiscoveryProductContextValueActionType {
  /** add a supplier company to the currently selected global supplier list */
  addSupplierToCurrentGlobalSupplierList: (
    companyKey: number,
    companyName: string,
    selectedList?: GlobalSupplierListType
  ) => void;
  /** remove a list from the database */
  removeList: (list: GlobalSupplierListType) => void;
  /** remove a supplier company from the currently selected global supplier list */
  removeSupplierFromCurrentGlobalSupplierList: (companyKey: number, companyName: string) => void;
  /** save a list to the database*/
  saveList: (list: GlobalSupplierListType, options?: SaveListOptions) => void;
  /** update selected list and fetch list of suppliers for a global supplier list */
  updateSelectedList: (listKey: number) => void;
  /** update list category and fetch a collection of list objects for a provided category */
  updateSelectedListCategory: (listCategoryId: ListCategoryIdType) => void;
  /** update selected company */
  updateSelectedListCompany: (companyKey: number) => void;
}

export interface PortalProductContextValueActionType {
  addSupplierGroupCompany: (
    supplierGroup: SupplierGroupType,
    company: SupplierGroupCompanyType,
    opts?: CreateSupplierGroupMemberOptions
  ) => void;
  addSupplierGroupPerson: (
    supplierGroup: SupplierGroupType,
    person: SupplierGroupCompanyPersonType,
    opts?: CreateSupplierGroupMemberOptions
  ) => void;
  createSupplierGroup: (companyKey: number, opts?: CreateSupplierGroupOptions) => void;
  createSupplierGroupCompany: (companyName: string, opts?: CreateSupplierGroupCompanyOptions) => void;
  createSupplierGroupPerson: (
    companyKey: number,
    person: CreateSupplierGroupCompanyPerson,
    opts?: CreateSupplierGroupCompanyPersonOptions
  ) => void;
  refetchSupplierGroups: () => void;
  removeSupplierGroup: (supplierGroup: SupplierGroupType) => void;
  removeSupplierCompanyFromSupplierGroup: (
    supplierGroup: SupplierGroupType,
    supplierCompany: SupplierGroupCompanyType
  ) => void;
  removeSupplierCompanyPersonFromSupplierGroup: (
    supplierGroup: SupplierGroupType,
    person: SupplierGroupCompanyPersonType
  ) => void;
  updateSupplierGroupMainContact: (supplierGroup: SupplierGroupType, person: SupplierGroupCompanyPersonType) => void;
  updateSupplierGroupName: (supplierGroup: SupplierGroupType, newSupplierGroupName: string) => void;
}

export interface SourcingProductContextValueActionType {
  /** create a new bid team */
  createBidTeam: (companyKey: number, opts?: CreateBidTeamOptions, companyKeys?: number[]) => void;
  /** create a bid team member */
  createBidTeamMember: (companyKey: number, opts?: CreateBidTeamMemberOptions) => void;
  /** refresh bid teams with server */
  refetchBidTeams: () => void;
  /** remove a bid team from a bid event */
  removeBidTeam: (bidTeam: any) => void;
  /** remove supplier company (member) from a bid team */
  removeSupplierCompanyFromBidTeam: (bidTeamSupplier: any, bidTeam: any) => void;
  /** remove person from a bid team */
  removeSupplierCompanyPersonFromBidTeam: (bidTeam: any, person: any) => void;
  /** send notifications to several people */
  sendBulkEmailNotifications: (personKeys: ReadonlyArray<number>) => void;
  /** sends an invite to people on bid teams */
  sendSupplierCompanyPersonFirstTimeNotification: (person: any) => void;
  /** sends an re-invite, notification or re-notification message to a person */
  sendSupplierCompanyPersonNotification: (person: any, opts?: SendSupplierCompanyPersonNotificationOptions) => void;
  /** invite a new supplier; also adds to bid teams */
  sendSupplierInvite: (formData: FormData) => void;
  /** change bid status for bid team */
  updateBidTeamApprovalStatus: (bidTeam: any, approvalStatus: string) => void;
  /** change bid intention for bid team */
  updateBidTeamBidIntention: (bidTeam: any, bidIntention: string) => void;
  /** change the main contact person for a bid team */
  updateBidTeamMainContact: (bidTeam: any, person: any) => void;
  /** change name of bid team */
  updateBidTeamName: (bidTeam: any, bidTeamName: string) => void;
  /** generic bid team update; can object several different properties */
  updateBidTeam: (bidTeam: any, opts?: UpdateSupplierTeamOptions) => void;
  createCompanyOnly: (formData: FormData) => Promise<{ companyKey: number }>;
}

type ProductContextValueActionType = {
  discoveryAction: DiscoveryProductContextValueActionType;
  portalAction: PortalProductContextValueActionType;
  sourcingAction: SourcingProductContextValueActionType;
};

type ProductContextValue = [productState: ProductContextState, productAction: ProductContextValueActionType];

interface ContractSetInitialDataAction {
  type: typeof CONTRACT_SET_INITIAL_DATA;
  productKey: EIPProductIdType;
  productType: ProductType;
}

interface DiscoveryCurrentListsSetLoadingAction {
  type: typeof DISCOVERY_CURRENT_LISTS_SET_LOADING;
  isLoading: boolean;
}

interface DiscoveryCurrentListsUpdateAction {
  type: typeof DISCOVERY_CURRENT_LISTS_UPDATE;
  lists: ReadonlyArray<GlobalSupplierListType>;
}

interface DiscoveryInitialDataSetLoadingAction {
  type: typeof DISCOVERY_INITIAL_DATA_SET_LOADING;
  isLoading: boolean;
}

interface GlobalSupplierListResetAction {
  type: typeof DISCOVERY_LIST_RESET;
  /** need to pass company key of logged in person to reducer to reset selected company key */
  personCompanyKey: number;
}

interface GlobalSupplierListSuppliersResetAction {
  type: typeof DISCOVERY_LIST_SUPPLIERS_RESET;
}

interface GlobalSupplierListSuppliersSetLoadingAction {
  type: typeof DISCOVERY_LIST_SUPPLIERS_SET_LOADING;
  isLoading: boolean;
}

interface GlobalSupplierListSuppliersUpdate {
  type: typeof DISCOVERY_LIST_SUPPLIERS_UPDATE;
  listSuppliers: ReadonlyArray<any>;
}

interface DiscoverySelectedCompanyUpdateAction {
  type: typeof DISCOVERY_SELECTED_COMPANY_UPDATE;
  companyKey: number;
}

interface DiscoverySelectedListCategoryUpdateAction {
  type: typeof DISCOVERY_SELECTED_LIST_CATEGORY_UPDATE;
  listCategoryId: ListCategoryIdType;
  /** used to reset company selection */
  personCompanyKey: number;
}

interface DiscoverySelectedListUpdateAction {
  type: typeof DISCOVERY_SELECTED_LIST_UPDATE;
  list?: GlobalSupplierListType;
}

interface DiscoverySetInitialDataAction {
  type: typeof DISCOVERY_SET_INITIAL_DATA;
  companies: ReadonlyArray<any>;
  createSupplierListPermissionGroups: ReadonlyArray<GlobalSupplierListPermissionGroupType>;
  currentSelectedListCategoryId: ListCategoryIdType;
  deleteSupplierListPermissionGroups: ReadonlyArray<GlobalSupplierListPermissionGroupType>;
  editSupplierListPermissionGroups: ReadonlyArray<GlobalSupplierListPermissionGroupType>;
  isDiscovery: boolean;
  lists: ReadonlyArray<any>;
  productKey: EIPProductIdType;
  productType: ProductType;
  viewSupplierListPermissionGroups: ReadonlyArray<GlobalSupplierListPermissionGroupType>;
}

interface PortalRefetchSupplierGroupsAction {
  type: typeof PORTAL_REFETCH_SUPPLIER_GROUPS;
  supplierGroups: ReadonlyArray<SupplierGroupType>;
  supplierVendorMappings: SupplierVendorMappingCollection;
}

interface PortalSetInitialDataAction {
  type: typeof PORTAL_SET_INITIAL_DATA;
  portalTitle: string;
  productKey: EIPProductIdType;
  productType: ProductType;
  supplierGroups: ReadonlyArray<any>;
  supplierVendorMappings: SupplierVendorMappingCollection;
}

interface PortalSetSupplierGroupsLoadingAction {
  type: typeof PORTAL_SET_SUPPLIER_GROUPS_LOADING;
  isLoading: boolean;
}

interface SourcingRefetchBidTeamsAction {
  type: typeof SOURCING_REFETCH_BID_TEAMS;
  bidTeams: ReadonlyArray<any>;
  supplierVendorMappings: SupplierVendorMappingCollection;
}

interface SourcingRefetchSupplierRequests {
  type: typeof SOURCING_REFETCH_SUPPLIER_REQUESTS;
  supplierRequestPersonCount: number;
  supplierRequests: ReadonlyArray<any>;
}

interface SourcingSetBidTeamsLoadingAction {
  type: typeof SOURCING_SET_BID_TEAMS_LOADING;
  isLoading: boolean;
}

interface SourcingSetInitialDataAction {
  type: typeof SOURCING_SET_INITIAL_DATA;
  bidEventTitle: string;
  bidTeams: ReadonlyArray<any>;
  productKey: EIPProductIdType;
  productType: ProductType;
  supplierRequestPersonCount: number;
  supplierRequests: ReadonlyArray<any>;
  supplierVendorMappings: SupplierVendorMappingCollection;
}

type ContractProductContextAction = ContractSetInitialDataAction;

type DiscoveryProductContextAction =
  | DiscoveryCurrentListsSetLoadingAction
  | DiscoveryCurrentListsUpdateAction
  | DiscoveryInitialDataSetLoadingAction
  | GlobalSupplierListResetAction
  | GlobalSupplierListSuppliersResetAction
  | GlobalSupplierListSuppliersSetLoadingAction
  | GlobalSupplierListSuppliersUpdate
  | DiscoverySelectedCompanyUpdateAction
  | DiscoverySelectedListCategoryUpdateAction
  | DiscoverySelectedListUpdateAction
  | DiscoverySetInitialDataAction;

type PortalProductContextAction =
  | PortalRefetchSupplierGroupsAction
  | PortalSetInitialDataAction
  | PortalSetSupplierGroupsLoadingAction;

type SourcingProductContextAction =
  | SourcingRefetchBidTeamsAction
  | SourcingRefetchSupplierRequests
  | SourcingSetBidTeamsLoadingAction
  | SourcingSetInitialDataAction;

type ProductContextAction =
  | ContractProductContextAction
  | DiscoveryProductContextAction
  | PortalProductContextAction
  | SourcingProductContextAction;

type DiscoveryListPermissionMapResponseType = Record<PermissionType, GlobalSupplierListPermissionGrantScopeMapType>;

//
// --- Default Context Object ---

const DEFAULT_PRODUCT_DATA: ProductContextState = {
  contract: {
    contractTitle: '',
    supplierTeams: []
  },
  discovery: {
    areCompaniesLoading: false,
    areListsLoading: false,
    areListSuppliersLoading: false,
    companies: [],
    companyKeysOnCurrentSelectedList: [],
    createSupplierListPermissionGroups: [],
    currentLists: [],
    currentListSuppliers: [],
    currentSelectedCompanyKey: 0,
    currentSelectedList: undefined,
    currentSelectedListCategoryId: LIST_CATEGORY_ID.COMPANY_LISTS,
    deleteSupplierListPermissionGroups: [],
    editSupplierListPermissionGroups: [],
    loggedInPersonCanDeleteCurrentSelectedList: false,
    loggedInPersonCanEditCurrentSelectedList: false,
    viewSupplierListPermissionGroups: []
  },
  isDiscovery: false,
  loggedInPersonCompany: {} as CompanyType, // this is overridden in initializeState, and the app won't load without it
  loggedInPersonKey: 0,
  portal: {
    areSupplierGroupsLoading: false,
    portalTitle: '',
    supplierGroups: [],
    supplierVendorMappings: []
  },
  productKey: undefined,
  productType: undefined,
  sourcing: {
    areBidTeamsLoading: false,
    bidEventTitle: '',
    bidTeams: [],
    supplierRequestPersonCount: 0,
    supplierRequests: [],
    supplierVendorMappings: []
  }
};

//
// --- Product Context ---

/**
 * Context for storing data and actions specific to the current product
 */
const ProductContext = React.createContext<ProductContextValue>([
  DEFAULT_PRODUCT_DATA,
  {
    discoveryAction: {
      addSupplierToCurrentGlobalSupplierList: noop,
      removeList: noop,
      removeSupplierFromCurrentGlobalSupplierList: noop,
      saveList: noop,
      updateSelectedList: noop,
      updateSelectedListCategory: noop,
      updateSelectedListCompany: noop
    },
    portalAction: {
      addSupplierGroupCompany: noop,
      addSupplierGroupPerson: noop,
      createSupplierGroup: noop,
      createSupplierGroupCompany: noop,
      createSupplierGroupPerson: noop,
      refetchSupplierGroups: noop,
      removeSupplierGroup: noop,
      removeSupplierCompanyFromSupplierGroup: noop,
      removeSupplierCompanyPersonFromSupplierGroup: noop,
      updateSupplierGroupMainContact: noop,
      updateSupplierGroupName: noop
    },
    sourcingAction: {
      createBidTeam: noop,
      createBidTeamMember: noop,
      refetchBidTeams: noop,
      removeBidTeam: noop,
      removeSupplierCompanyFromBidTeam: noop,
      removeSupplierCompanyPersonFromBidTeam: noop,
      sendBulkEmailNotifications: noop,
      sendSupplierCompanyPersonFirstTimeNotification: noop,
      sendSupplierCompanyPersonNotification: noop,
      sendSupplierInvite: noop,
      updateBidTeamApprovalStatus: noop,
      updateBidTeamBidIntention: noop,
      updateBidTeamMainContact: noop,
      updateBidTeamName: noop,
      updateBidTeam: noop,
      createCompanyOnly: async (_formData: FormData) => ({ companyKey: 0 })
    }
  }
]);

//
// --- Product Context Reducer ---

const CONTRACT_SET_INITIAL_DATA = 'contract-set-initial-data';

const DISCOVERY_CURRENT_LISTS_SET_LOADING = 'discovery-current-lists-set-loading';
const DISCOVERY_CURRENT_LISTS_UPDATE = 'discovery-current-lists-update';
const DISCOVERY_INITIAL_DATA_SET_LOADING = 'discovery-initial-data-set-loading';
const DISCOVERY_LIST_RESET = 'global-supplier-list-reset';
const DISCOVERY_LIST_SUPPLIERS_RESET = 'global-supplier-list-suppliers-reset';
const DISCOVERY_LIST_SUPPLIERS_SET_LOADING = 'global-supplier-list-suppliers-set-loading';
const DISCOVERY_LIST_SUPPLIERS_UPDATE = 'global-supplier-list-suppliers-update';
const DISCOVERY_SELECTED_COMPANY_UPDATE = 'discovery-selected-company-update';
const DISCOVERY_SELECTED_LIST_CATEGORY_UPDATE = 'discovery-selected-list-category-update';
const DISCOVERY_SELECTED_LIST_UPDATE = 'discovery-selected-list-update';
const DISCOVERY_SET_INITIAL_DATA = 'discovery-set-initial-data';

const PORTAL_REFETCH_SUPPLIER_GROUPS = 'portal-refetch-supplier-groups';
const PORTAL_SET_SUPPLIER_GROUPS_LOADING = 'portal-set-supplier-groups-loading';
const PORTAL_SET_INITIAL_DATA = 'portal-set-initial-data';

const SOURCING_REFETCH_BID_TEAMS = 'sourcing-refetch-bid-teams';
const SOURCING_REFETCH_SUPPLIER_REQUESTS = 'sourcing-refetch-supplier-requests';
const SOURCING_SET_BID_TEAMS_LOADING = 'sourcing-set-bid-teams-loading';
const SOURCING_SET_INITIAL_DATA = 'sourcing-set-initial-data';

// default state
const defaultState: ProductContextState = {
  ...DEFAULT_PRODUCT_DATA
};

// initialize state
const initializeState =
  ({ personCompany, personKey }: InitializeStateOptions) =>
  (defaultState: ProductContextState): ProductContextState => {
    return {
      ...defaultState,
      loggedInPersonCompany: personCompany,
      loggedInPersonKey: personKey,
      discovery: {
        ...defaultState.discovery,
        currentSelectedCompanyKey: personCompany.companyKey // used logged in person key as default selection
      }
    };
  };

// reducer
function productContextReducer(state: ProductContextState, action: ProductContextAction): ProductContextState {
  switch (action.type) {
    case CONTRACT_SET_INITIAL_DATA:
      return { ...state, productKey: action.productKey, productType: PRODUCT_TYPE.CONTRACT };

    case DISCOVERY_CURRENT_LISTS_SET_LOADING:
      return { ...state, discovery: { ...state.discovery, areListsLoading: action.isLoading } };
    case DISCOVERY_CURRENT_LISTS_UPDATE:
      return {
        ...state,
        discovery: {
          ...state.discovery,
          currentLists: Array.isArray(action.lists)
            ? action.lists.sort((listA, listB) => caseInsensitiveLocaleCompare(listA.listName, listB.listName))
            : defaultState.discovery.currentLists
        }
      };
    case DISCOVERY_INITIAL_DATA_SET_LOADING:
      return {
        ...state,
        discovery: { ...state.discovery, areCompaniesLoading: action.isLoading, areListsLoading: action.isLoading }
      };
    case DISCOVERY_LIST_RESET:
      return {
        ...state,
        discovery: {
          ...state.discovery,
          companyKeysOnCurrentSelectedList: defaultState.discovery.companyKeysOnCurrentSelectedList,
          currentLists: defaultState.discovery.currentLists,
          currentListSuppliers: defaultState.discovery.currentListSuppliers,
          currentSelectedCompanyKey: action.personCompanyKey,
          currentSelectedList: defaultState.discovery.currentSelectedList,
          loggedInPersonCanDeleteCurrentSelectedList: defaultState.discovery.loggedInPersonCanDeleteCurrentSelectedList,
          loggedInPersonCanEditCurrentSelectedList: defaultState.discovery.loggedInPersonCanEditCurrentSelectedList
        }
      };
    case DISCOVERY_LIST_SUPPLIERS_RESET:
      return {
        ...state,
        discovery: { ...state.discovery, currentListSuppliers: defaultState.discovery.currentListSuppliers }
      };
    case DISCOVERY_LIST_SUPPLIERS_SET_LOADING:
      return { ...state, discovery: { ...state.discovery, areListSuppliersLoading: action.isLoading } };
    case DISCOVERY_LIST_SUPPLIERS_UPDATE:
      return {
        ...state,
        discovery: {
          ...state.discovery,
          companyKeysOnCurrentSelectedList:
            Array.isArray(action.listSuppliers) && action.listSuppliers.length > 0
              ? getCompanyKeysFromGroupedSupplierList(action.listSuppliers)
              : defaultState.discovery.companyKeysOnCurrentSelectedList,
          currentListSuppliers: action.listSuppliers
        }
      };
    case DISCOVERY_SELECTED_COMPANY_UPDATE:
      return {
        ...state,
        // clear previous selected list and list suppliers when company changed
        discovery: {
          ...state.discovery,
          companyKeysOnCurrentSelectedList: defaultState.discovery.companyKeysOnCurrentSelectedList,
          currentListSuppliers: defaultState.discovery.currentListSuppliers,
          currentSelectedCompanyKey: action.companyKey,
          currentSelectedList: defaultState.discovery.currentSelectedList,
          loggedInPersonCanDeleteCurrentSelectedList: defaultState.discovery.loggedInPersonCanDeleteCurrentSelectedList,
          loggedInPersonCanEditCurrentSelectedList: defaultState.discovery.loggedInPersonCanEditCurrentSelectedList
        }
      };
    case DISCOVERY_SELECTED_LIST_CATEGORY_UPDATE:
      return {
        ...state,
        // clear previous list names, reset selected company, and clear list suppliers when category changed
        discovery: {
          ...state.discovery,
          companyKeysOnCurrentSelectedList: defaultState.discovery.companyKeysOnCurrentSelectedList,
          currentLists: defaultState.discovery.currentLists,
          currentListSuppliers: defaultState.discovery.currentListSuppliers,
          currentSelectedCompanyKey: action.personCompanyKey,
          currentSelectedList: defaultState.discovery.currentSelectedList,
          currentSelectedListCategoryId: action.listCategoryId,
          loggedInPersonCanDeleteCurrentSelectedList: defaultState.discovery.loggedInPersonCanDeleteCurrentSelectedList,
          loggedInPersonCanEditCurrentSelectedList: defaultState.discovery.loggedInPersonCanEditCurrentSelectedList
        }
      };
    case DISCOVERY_SELECTED_LIST_UPDATE:
      return {
        ...state,
        discovery: {
          ...state.discovery,
          currentSelectedList: action.list,
          loggedInPersonCanDeleteCurrentSelectedList: action.list
            ? isListActionAllowedByPermissionGroups(
                state.discovery.deleteSupplierListPermissionGroups,
                action.list,
                state.loggedInPersonKey,
                state.loggedInPersonCompany?.companyKey || 0
              )
            : defaultState.discovery.loggedInPersonCanEditCurrentSelectedList,
          loggedInPersonCanEditCurrentSelectedList: action.list
            ? isListActionAllowedByPermissionGroups(
                state.discovery.editSupplierListPermissionGroups,
                action.list,
                state.loggedInPersonKey,
                state.loggedInPersonCompany?.companyKey || 0
              )
            : defaultState.discovery.loggedInPersonCanEditCurrentSelectedList
        }
      };
    case DISCOVERY_SET_INITIAL_DATA:
      return {
        ...state,
        discovery: {
          ...state.discovery,
          companies: action.companies,
          createSupplierListPermissionGroups: action.createSupplierListPermissionGroups,
          currentLists: Array.isArray(action.lists)
            ? action.lists.sort((listA, listB) => caseInsensitiveLocaleCompare(listA.listName, listB.listName))
            : defaultState.discovery.currentLists,
          currentSelectedListCategoryId: action.currentSelectedListCategoryId,
          deleteSupplierListPermissionGroups: action.deleteSupplierListPermissionGroups,
          editSupplierListPermissionGroups: action.editSupplierListPermissionGroups,
          loggedInPersonCanDeleteCurrentSelectedList: areAllAllowedByPermissionGroups(
            action.deleteSupplierListPermissionGroups
          ),
          loggedInPersonCanEditCurrentSelectedList: areAllAllowedByPermissionGroups(
            action.editSupplierListPermissionGroups
          ),
          viewSupplierListPermissionGroups: action.viewSupplierListPermissionGroups
        },
        isDiscovery: action.isDiscovery,
        productKey: action.productKey,
        productType: action.productType
      };

    case PORTAL_REFETCH_SUPPLIER_GROUPS:
      return {
        ...state,
        portal: {
          ...state.portal,
          supplierGroups: action.supplierGroups,
          supplierVendorMappings: action.supplierVendorMappings
        }
      };

    case PORTAL_SET_INITIAL_DATA:
      return {
        ...state,
        portal: {
          ...state.portal,
          portalTitle: action.portalTitle,
          supplierGroups: action.supplierGroups,
          supplierVendorMappings: action.supplierVendorMappings
        },
        productKey: action.productKey,
        productType: action.productType
      };

    case PORTAL_SET_SUPPLIER_GROUPS_LOADING:
      return { ...state, portal: { ...state.portal, areSupplierGroupsLoading: action.isLoading } };

    case SOURCING_REFETCH_BID_TEAMS:
      return {
        ...state,
        sourcing: {
          ...state.sourcing,
          bidTeams: action.bidTeams,
          supplierVendorMappings: action.supplierVendorMappings
        }
      };

    case SOURCING_REFETCH_SUPPLIER_REQUESTS:
      return {
        ...state,
        sourcing: {
          ...state.sourcing,
          supplierRequestPersonCount: action.supplierRequestPersonCount,
          supplierRequests: action.supplierRequests
        }
      };

    case SOURCING_SET_BID_TEAMS_LOADING:
      return { ...state, sourcing: { ...state.sourcing, areBidTeamsLoading: action.isLoading } };

    case SOURCING_SET_INITIAL_DATA:
      return {
        ...state,
        productKey: action.productKey,
        productType: action.productType,
        sourcing: {
          ...state.sourcing,
          bidEventTitle: action.bidEventTitle,
          bidTeams: action.bidTeams,
          supplierRequestPersonCount: action.supplierRequestPersonCount,
          supplierRequests: action.supplierRequests,
          supplierVendorMappings: action.supplierVendorMappings
        }
      };
    default:
      return state;
  }
}

//
// --- Helper Functions ---

function getListCategoryItem(listCategoryId: ListCategoryIdType): ListCategoryMapItemType | undefined {
  return ListCategoryMap[listCategoryId];
}

function getGlobalSupplierListCategoryItem(
  listCategoryId: ListCategoryIdType
): GlobalSupplierListCategoryItemType | undefined {
  const listCategoryItem = getListCategoryItem(listCategoryId);
  return listCategoryItem && isGlobalSupplierListCategoryItem(listCategoryItem) ? listCategoryItem : undefined;
}

async function getGlobalSupplierListSupplierVendorMappings(list: GlobalSupplierListType) {
  const eipCompanyKeys = Array.isArray(list.miListAssignments)
    ? list.miListAssignments.map(assignment => assignment.numericKey1)
    : [];

  const supplierVendorMappingResponse = await axios.request(
    APIConfig.Supplier.getSupplierVendorMappings(eipCompanyKeys)
  );

  return supplierVendorMappingResponse && Array.isArray(supplierVendorMappingResponse.data)
    ? (supplierVendorMappingResponse.data as ReadonlyArray<any>)
    : [];
}

async function loadDiscoveryPermissions(productKey: EIPProductIdType): Promise<DiscoveryListPermissionMapResponseType> {
  const listPermissionResponse = await axios.request(
    APIConfig.SupplierList.getSupplierListPermissionsForProduct(productKey)
  );

  return listPermissionResponse && listPermissionResponse.data ? listPermissionResponse.data : {};
}

async function loadGlobalSupplierListsForOtherCompany(
  companyKey: number
): Promise<ReadonlyArray<GlobalSupplierListType>> {
  const listResponse = await axios.request(
    APIConfig.SupplierList.getSupplierListsByCategory(
      otherCompanyListsCategoryItem.globalSupplierListCategory,
      otherCompanyListsCategoryItem.globalSupplierListType
    )
  );

  return Array.isArray(listResponse.data)
    ? listResponse.data.filter(list => list.ownerCompany.companyKey === companyKey)
    : [];
}

async function loadGlobalSupplierListsForSameCompany(listCategoryItem: GlobalSupplierListCategoryItemType) {
  const { globalSupplierListCategory, globalSupplierListType } = listCategoryItem;

  const listResponse = await axios.request(
    APIConfig.SupplierList.getSupplierListsByCategory(globalSupplierListCategory, globalSupplierListType)
  );

  return Array.isArray(listResponse.data) ? listResponse.data : [];
}

async function loadSourcingProductData(offeringKey: number): Promise<{
  bidEvent: any;
  bidTeams: ReadonlyArray<any>;
  supplierRequestPersonCount: number;
  supplierRequests: ReadonlyArray<any>;
  supplierVendorMappings: SupplierVendorMappingCollection;
}> {
  // can't load all requests in parallel because bidTeams are needed to build supplierVendorMappings
  // load bidTeams first, then run the rest in parallel

  // Disable caching with timestamp - caching the bidTeams causes UI to not refresh when it should in IE11.
  const bidTeamsResponse = await axios.request(
    APIConfig.Sourcing.getBidTeams(offeringKey, { useCacheBustTimestamp: true })
  );
  const memberCompanyKeys = getBidTeamMemberCompanyKeys(bidTeamsResponse.data);

  const [bidEventResponse, supplierRequestsResponse, supplierVendorMappingsResponse] = await Promise.all([
    axios.request(APIConfig.Sourcing.getBidEvent(offeringKey)),
    axios.request(APIConfig.Sourcing.getSupplierRequests(offeringKey)),
    axios.request(APIConfig.Supplier.getSupplierVendorMappings(memberCompanyKeys))
  ]);

  const supplierRequestsByCompany = groupSupplierRequestsByCompany(supplierRequestsResponse.data);

  return {
    bidEvent: bidEventResponse.data,
    bidTeams: transformToCuratedBidTeams(bidTeamsResponse.data, supplierVendorMappingsResponse.data),
    supplierRequestPersonCount: getPersonCountForSupplierRequestsByCompany(supplierRequestsByCompany),
    supplierRequests: supplierRequestsByCompany,
    supplierVendorMappings: supplierVendorMappingsResponse.data
  };
}

async function loadPortalProductData(portalId: number): Promise<{
  portalTitle: string;
  supplierGroups: ReadonlyArray<any>;
  supplierVendorMappings: SupplierVendorMappingCollection;
}> {
  // can't load all requests in parallel because supplierGroups are needed to build supplierVendorMappings
  // load supplierGroups first, then run the rest in parallel

  // Disable caching with timestamp - caching the supplierGroups causes UI to not refresh when it should in IE11.
  const supplierGroupsResponse = await axios.request(
    APIConfig.Portal.getSupplierGroups(portalId, { useCacheBustTimestamp: true })
  );
  const companyKeys = getCompanyKeysFromRawSupplierGroups(supplierGroupsResponse.data);

  const [portalResponse, supplierVendorMappingsResponse] = await Promise.all([
    axios.request(APIConfig.Portal.getPortal(portalId)),
    axios.request(APIConfig.Supplier.getSupplierVendorMappings(companyKeys))
  ]);

  const supplierVendorMappings = supplierVendorMappingsResponse ? supplierVendorMappingsResponse.data : [];

  return {
    portalTitle: portalResponse && portalResponse.data ? portalResponse.data.portaltitle : '',
    supplierGroups: transformToCuratedSupplierGroups(supplierGroupsResponse.data, supplierVendorMappings),
    supplierVendorMappings
  };
}

async function loadBidTeams(
  offeringKey: number
): Promise<{ bidTeams: ReadonlyArray<any>; supplierVendorMappings: SupplierVendorMappingCollection }> {
  // Disable caching with timestamp - caching the bidTeams causes UI to not refresh when it should in IE11.
  const bidTeamsResponse = await axios.request(
    APIConfig.Sourcing.getBidTeams(offeringKey, { useCacheBustTimestamp: true })
  );
  const memberCompanyKeys = getBidTeamMemberCompanyKeys(bidTeamsResponse.data);

  const supplierVendorMappingsResponse = await axios.request(
    APIConfig.Supplier.getSupplierVendorMappings(memberCompanyKeys)
  );

  return {
    bidTeams: transformToCuratedBidTeams(bidTeamsResponse.data, supplierVendorMappingsResponse.data),
    supplierVendorMappings: supplierVendorMappingsResponse.data
  };
}

async function loadSupplierGroups(portalId: number): Promise<{
  supplierGroups: ReadonlyArray<SupplierGroupType>;
  supplierVendorMappings: SupplierVendorMappingCollection;
}> {
  const supplierGroupsResponse = await axios.request(
    APIConfig.Portal.getSupplierGroups(portalId, { useCacheBustTimestamp: true })
  );
  const companyKeys = getCompanyKeysFromRawSupplierGroups(supplierGroupsResponse.data);

  const supplierVendorMappingsResponse = await axios.request(APIConfig.Supplier.getSupplierVendorMappings(companyKeys));
  const supplierVendorMappings = supplierVendorMappingsResponse.data ? supplierVendorMappingsResponse.data : {};

  return {
    supplierGroups: transformToCuratedSupplierGroups(supplierGroupsResponse.data, supplierVendorMappings),
    supplierVendorMappings: supplierVendorMappings
  };
}

async function loadSupplierRequestsByCompany(offeringKey: number): Promise<any> {
  const supplierRequestResponse = await axios.request(APIConfig.Sourcing.getSupplierRequests(offeringKey));

  return Array.isArray(supplierRequestResponse.data)
    ? groupSupplierRequestsByCompany(supplierRequestResponse.data)
    : [];
}

//
// --- Product Context Provider ---

/**
 * Product context provider component
 *
 * Sets context value to an array where the first element is a data object and the second
 * element is an action object (or object with a bunch of functions to trigger actions on product data)
 */
export const ProductContextProvider: React.FC<ProductContextProviderProps> = props => {
  const { children, personCompany, personKey } = props;

  const [state, dispatch] = React.useReducer(
    productContextReducer,
    defaultState,
    initializeState({ personCompany, personKey })
  );
  const [eventBusClient] = useEventBus({
    [EVENT.REFRESH_BID_TEAMS]: () => refetchSourcingBidTeams(),
    [EVENT.REFRESH_SUPPLIER_REQUESTS]: () => refetchSupplierRequests()
  });

  const { isDiscovery, offeringKey, portalId, productKey, productType } = useQueryParameters();
  const { enqueueSnackbar } = useSnackbar();

  React.useEffect(() => {
    async function requestProductData() {
      if (productType === PRODUCT_TYPE.CONTRACT) {
        dispatch({ type: CONTRACT_SET_INITIAL_DATA, productKey, productType });
      } else if (isDiscovery) {
        dispatch({ type: DISCOVERY_INITIAL_DATA_SET_LOADING, isLoading: true });

        // first load permissions and create permission group arrays
        const discoveryPermissionMap = await loadDiscoveryPermissions(productKey);

        const createPermissionGroups = convertGrantScopeMapToPermissionGroups(
          discoveryPermissionMap[PERMISSION.GRANT_CREATE]
        );
        const deletePermissionGroups = convertGrantScopeMapToPermissionGroups(
          discoveryPermissionMap[PERMISSION.GRANT_DELETE]
        );
        const editPermissionGroups = convertGrantScopeMapToPermissionGroups(
          discoveryPermissionMap[PERMISSION.GRANT_EDIT]
        );
        const viewPermissionGroups = convertGrantScopeMapToPermissionGroups(
          discoveryPermissionMap[PERMISSION.GRANT_VIEW]
        );

        // now that we have permissions, determine which type of lists to load first
        // ALL and COMPANY_OWNER should see My Company lists. OWNER (only) should see My Lists

        const baseInitialDataAction: DiscoverySetInitialDataAction = {
          type: DISCOVERY_SET_INITIAL_DATA,
          companies: defaultState.discovery.companies,
          createSupplierListPermissionGroups: createPermissionGroups,
          currentSelectedListCategoryId: defaultState.discovery.currentSelectedListCategoryId,
          deleteSupplierListPermissionGroups: deletePermissionGroups,
          editSupplierListPermissionGroups: editPermissionGroups,
          isDiscovery,
          lists: defaultState.discovery.currentLists,
          productKey,
          productType: productType ? productType : PRODUCT_TYPE.MARKET,
          viewSupplierListPermissionGroups: viewPermissionGroups
        };

        if (!Array.isArray(viewPermissionGroups) || !viewPermissionGroups.length) {
          // if no view permissions, do not load anything
          dispatch(baseInitialDataAction);
        } else if (
          viewPermissionGroups.length === 1 &&
          viewPermissionGroups.includes(GLOBAL_SUPPLIER_LISTS_PERMISSION_GROUP.OWNER)
        ) {
          // if view contains owner only, load my lists (no companies needed)
          const lists = await loadGlobalSupplierListsForSameCompany(myListsCategoryItem);

          dispatch({
            ...baseInitialDataAction,
            currentSelectedListCategoryId: LIST_CATEGORY_ID.MY_LISTS,
            lists
          });
        } else {
          // if view contains company owner or all, load company lists and all data for all companies
          const [companiesResponse, listResponse] = await Promise.all([
            axios.request(APIConfig.Sourcing.getUniqueBuyerCompanies()),
            axios.request(
              APIConfig.SupplierList.getSupplierListsByCategory(
                companyListsCategoryItem.globalSupplierListCategory,
                companyListsCategoryItem.globalSupplierListType
              )
            )
          ]);

          dispatch({
            ...baseInitialDataAction,
            companies: Array.isArray(companiesResponse.data) ? companiesResponse.data : [],
            currentSelectedListCategoryId: defaultState.discovery.currentSelectedListCategoryId,
            lists: Array.isArray(listResponse.data) ? listResponse.data : []
          } as DiscoverySetInitialDataAction);
        }

        dispatch({ type: DISCOVERY_INITIAL_DATA_SET_LOADING, isLoading: false });
      } else if (productType === PRODUCT_TYPE.PORTAL && portalId) {
        try {
          const { portalTitle, supplierGroups, supplierVendorMappings } = await loadPortalProductData(portalId);
          dispatch({
            type: PORTAL_SET_INITIAL_DATA,
            portalTitle,
            productKey,
            productType,
            supplierGroups,
            supplierVendorMappings
          });
        } catch (error) {
          eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
            message: createRequestErrorMessage(
              'Error while fetching information for this portal',
              axios.isAxiosError(error) ? error.response : undefined
            )
          });
        }
      } else if (productType === PRODUCT_TYPE.SOURCING && offeringKey) {
        try {
          const { bidEvent, bidTeams, supplierRequestPersonCount, supplierRequests, supplierVendorMappings } =
            await loadSourcingProductData(offeringKey);

          dispatch({
            type: SOURCING_SET_INITIAL_DATA,
            bidEventTitle: bidEvent.offeringTitle,
            bidTeams,
            productKey,
            productType,
            supplierRequestPersonCount,
            supplierRequests,
            supplierVendorMappings
          });
        } catch (error) {
          eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
            message: createRequestErrorMessage(
              'Error while fetching information for this bid event',
              axios.isAxiosError(error) ? error.response : undefined
            )
          });
        }
      } else {
        throw Error(`Product context not setup for product of type: ${productType}`);
      }
    }

    requestProductData();
  }, [eventBusClient, isDiscovery, offeringKey, portalId, productKey, productType]);

  //
  // --- Helper Functions ---

  const executeRefreshGlobalSupplierLists = React.useCallback(
    async (opts: ExecuteRefreshGlobalSupplierListsOptions = {}) => {
      const { hasSelectedListBeenRemoved = false } = opts;

      // after any changes, we need to update currentLists and currentSelectedList along with any
      // auxillary data

      try {
        dispatch({ type: DISCOVERY_LIST_SUPPLIERS_SET_LOADING, isLoading: true });

        const companyKey = state.discovery.currentSelectedCompanyKey;
        const previousList = state.discovery.currentSelectedList;

        const globalSupplierListCategory = getGlobalSupplierListCategoryItem(
          state.discovery.currentSelectedListCategoryId
        );

        if (!globalSupplierListCategory || !previousList) {
          return;
        }

        const [lists, listSupplierResponse] = await Promise.all([
          companyKey === personCompany.companyKey
            ? loadGlobalSupplierListsForSameCompany(globalSupplierListCategory)
            : companyKey
            ? loadGlobalSupplierListsForOtherCompany(companyKey)
            : Promise.resolve([]),
          !hasSelectedListBeenRemoved
            ? axios.request(APIConfig.SupplierList.getSupplierListResults(previousList.listKey))
            : Promise.resolve(null)
        ]);

        const updatedList = lists.find(list => list.listKey === previousList.listKey);
        const supplierVendorMappings =
          !hasSelectedListBeenRemoved && updatedList
            ? await getGlobalSupplierListSupplierVendorMappings(updatedList)
            : [];
        // update currentLists (collection of all available lists)
        dispatch({ type: DISCOVERY_CURRENT_LISTS_UPDATE, lists });

        // update current selected list
        if (hasSelectedListBeenRemoved) {
          dispatch({ type: DISCOVERY_SELECTED_LIST_UPDATE, list: defaultState.discovery.currentSelectedList });
        } else if (updatedList) {
          dispatch({ type: DISCOVERY_SELECTED_LIST_UPDATE, list: updatedList });
        }

        // update suppliers associated with current selected list
        dispatch({
          type: DISCOVERY_LIST_SUPPLIERS_UPDATE,
          listSuppliers:
            hasSelectedListBeenRemoved || !listSupplierResponse
              ? defaultState.discovery.currentListSuppliers
              : transformSupplierListMembersToSearchResult(listSupplierResponse.data, supplierVendorMappings, {
                  excludePeople: true
                })
        });
      } catch (error) {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(`Error while refreshing global supplier lists`)
        });
      } finally {
        dispatch({ type: DISCOVERY_LIST_SUPPLIERS_SET_LOADING, isLoading: false });
      }
    },
    [
      eventBusClient,
      personCompany.companyKey,
      state.discovery.currentSelectedCompanyKey,
      state.discovery.currentSelectedList,
      state.discovery.currentSelectedListCategoryId
    ]
  );

  /**
   * run request to load global supplier lists that are available for the current selected global supplier list category
   * (company lists or my lists) taking selected company into account if necessary
   */
  const executeGlobalSupplierListsRequest = React.useCallback(
    async (
      globalSupplierListCategoryItem: GlobalSupplierListCategoryItemType,
      options: ExecuteGlobalSupplierListsRequestOptions = {}
    ) => {
      const {
        afterExecute = noop,
        companyKey,
        newSelectedListKey,
        shouldUpdateSelectedCompanyKey = false,
        shouldUpdateSelectedList = false,
        shouldUpdateSelectedListCategory = false
      } = options;

      try {
        dispatch({ type: DISCOVERY_CURRENT_LISTS_SET_LOADING, isLoading: true });

        if (shouldUpdateSelectedListCategory) {
          dispatch({
            type: DISCOVERY_SELECTED_LIST_CATEGORY_UPDATE,
            listCategoryId: globalSupplierListCategoryItem.id,
            personCompanyKey: personCompany.companyKey
          });
        }
        const lists: ReadonlyArray<GlobalSupplierListType> =
          companyKey === personCompany.companyKey
            ? await loadGlobalSupplierListsForSameCompany(globalSupplierListCategoryItem)
            : companyKey
            ? await loadGlobalSupplierListsForOtherCompany(companyKey)
            : [];

        dispatch({ type: DISCOVERY_CURRENT_LISTS_UPDATE, lists });

        if (shouldUpdateSelectedCompanyKey && companyKey) {
          dispatch({ type: DISCOVERY_SELECTED_COMPANY_UPDATE, companyKey });
        }

        if (shouldUpdateSelectedList) {
          const newList = lists.find(list => list.listKey === newSelectedListKey);

          if (newList) {
            dispatch({ type: DISCOVERY_SELECTED_LIST_UPDATE, list: newList });
          }
        }

        afterExecute();
      } catch (error) {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(`Error while loading global supplier lists`)
        });
      } finally {
        dispatch({ type: DISCOVERY_CURRENT_LISTS_SET_LOADING, isLoading: false });
      }
    },
    [eventBusClient, personCompany.companyKey]
  );

  const executeGlobalSupplierListSuppliersRequest = React.useCallback(
    async (listKey: number, opts: ExecuteGlobalSupplierListSuppliersRequestOptions = {}) => {
      const { afterExecute = noop, globalSupplierList } = opts;
      const list = globalSupplierList
        ? globalSupplierList
        : state.discovery.currentLists.find(list => list.listKey === listKey);
      try {
        dispatch({ type: DISCOVERY_LIST_SUPPLIERS_SET_LOADING, isLoading: true });
        const listSupplierResponse = await axios.request(APIConfig.SupplierList.getSupplierListResults(listKey));
        const supplierVendorMappings = list ? await getGlobalSupplierListSupplierVendorMappings(list) : [];

        dispatch({
          type: DISCOVERY_LIST_SUPPLIERS_UPDATE,
          listSuppliers: transformSupplierListMembersToSearchResult(listSupplierResponse.data, supplierVendorMappings, {
            excludePeople: true
          })
        });

        afterExecute();
      } catch (error) {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            'Error while fetching global supplier list suppliers',
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
      } finally {
        dispatch({ type: DISCOVERY_LIST_SUPPLIERS_SET_LOADING, isLoading: false });
      }
    },
    [eventBusClient, state.discovery.currentLists]
  );

  const executeRefetchSourcingBidTeams = React.useCallback(async () => {
    if (!offeringKey) {
      return;
    }

    try {
      eventBusClient.sendEvent(EVENT.SHOW_BID_TEAM_PANEL_LOADING_MASK, true);
      const { bidTeams, supplierVendorMappings } = await loadBidTeams(offeringKey);
      dispatch({ type: SOURCING_REFETCH_BID_TEAMS, bidTeams, supplierVendorMappings });
      eventBusClient.sendEvent(EVENT.SHOW_BID_TEAM_PANEL_LOADING_MASK, false);
    } catch (error) {
      eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
        message: createRequestErrorMessage(
          'Error while fetching bid teams',
          axios.isAxiosError(error) ? error.response : undefined
        )
      });
    }
  }, [eventBusClient, offeringKey]);

  const executeRefetchPortalSupplierGroups = React.useCallback(
    async (opts: ExecuteRefetchPortalSupplierGroupOptions = {}) => {
      const { afterExecute = noop } = opts;

      if (!portalId) {
        return;
      }

      dispatch({ type: PORTAL_SET_SUPPLIER_GROUPS_LOADING, isLoading: true });

      try {
        const { supplierGroups, supplierVendorMappings } = await loadSupplierGroups(portalId);
        dispatch({ type: PORTAL_REFETCH_SUPPLIER_GROUPS, supplierGroups, supplierVendorMappings });
        afterExecute();
      } catch (error) {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            'Error while fetching bid teams',
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
      } finally {
        dispatch({ type: PORTAL_SET_SUPPLIER_GROUPS_LOADING, isLoading: false });
      }
    },
    [eventBusClient, portalId]
  );

  //
  // --- Product Action Functions ---

  const addPortalSupplierGroupCompany = React.useCallback(
    async (
      supplierGroup: SupplierGroupType,
      company: SupplierGroupCompanyType,
      opts: CreateSupplierGroupMemberOptions = {}
    ) => {
      const { afterCreate = noop } = opts;

      if (!portalId) {
        return;
      }

      dispatch({ type: PORTAL_SET_SUPPLIER_GROUPS_LOADING, isLoading: true });

      try {
        await axios.request(APIConfig.Portal.addCompanyToSupplierGroup(portalId, supplierGroup.id, company.companyKey));
        enqueueSnackbar(`${company.name} has been added to supplier group ${supplierGroup.name}.`, {
          variant: 'success'
        });
        executeRefetchPortalSupplierGroups();
        afterCreate();
      } catch (error) {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            `Error adding company to supplier group "${supplierGroup.name}"`,
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
        dispatch({ type: PORTAL_SET_SUPPLIER_GROUPS_LOADING, isLoading: false });
      }
    },
    [enqueueSnackbar, eventBusClient, executeRefetchPortalSupplierGroups, portalId]
  );

  const addPortalSupplierGroupPerson = React.useCallback(
    async (
      supplierGroup: SupplierGroupType,
      person: SupplierGroupCompanyPersonType,
      opts: CreateSupplierGroupMemberOptions = {}
    ) => {
      const { afterCreate = noop } = opts;

      if (!portalId) {
        return;
      }

      dispatch({ type: PORTAL_SET_SUPPLIER_GROUPS_LOADING, isLoading: true });

      try {
        await axios.request(APIConfig.Portal.addPersonToSupplierGroup(portalId, supplierGroup.id, person.personKey));
        enqueueSnackbar(`${person.name} has been added to supplier group ${supplierGroup.name}.`, {
          variant: 'success'
        });
        executeRefetchPortalSupplierGroups();
        afterCreate();
      } catch (error) {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            `Error adding person to supplier group "${supplierGroup.name}"`,
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
        dispatch({ type: PORTAL_SET_SUPPLIER_GROUPS_LOADING, isLoading: false });
      }
    },
    [enqueueSnackbar, eventBusClient, executeRefetchPortalSupplierGroups, portalId]
  );

  const addSupplierToCurrentGlobalSupplierList = React.useCallback(
    async (companyKey: number, companyName: string, selectedList?: GlobalSupplierListType) => {
      const currentSelectedList = state.discovery.currentSelectedList || selectedList;
      if (!currentSelectedList) {
        return;
      }

      try {
        await axios.request(
          APIConfig.SupplierList.addGlobalSupplierListAssignments(currentSelectedList, [companyKey], productKey)
        );

        enqueueSnackbar(`${companyName} has been added to ${currentSelectedList.listName}.`, {
          variant: 'success'
        });

        executeRefreshGlobalSupplierLists();
      } catch (error) {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            'There was an error adding suppliers to this list',
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
      }
    },
    [
      enqueueSnackbar,
      eventBusClient,
      executeRefreshGlobalSupplierLists,
      state.discovery.currentSelectedList,
      productKey
    ]
  );

  const createPortalSupplierGroup = React.useCallback(
    async (companyKey: number, opts: CreateSupplierGroupOptions = {}) => {
      const { afterCreate = noop, personKey } = opts;

      if (!portalId) {
        return;
      }

      dispatch({ type: PORTAL_SET_SUPPLIER_GROUPS_LOADING, isLoading: true });

      try {
        await axios.request(
          APIConfig.Portal.createSupplierGroup(portalId, companyKey, personKey ? { personKey } : undefined)
        );
        executeRefetchPortalSupplierGroups({ afterExecute: afterCreate });
      } catch {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage('Error adding supplier group')
        });
        dispatch({ type: PORTAL_SET_SUPPLIER_GROUPS_LOADING, isLoading: false });
      }
    },
    [eventBusClient, portalId, executeRefetchPortalSupplierGroups]
  );

  const createPortalSupplierGroupCompany = React.useCallback(
    async (companyName: string, opts: CreateSupplierGroupCompanyOptions = {}) => {
      const { groupId, person } = opts;

      if (!portalId) {
        return;
      }

      dispatch({ type: PORTAL_SET_SUPPLIER_GROUPS_LOADING, isLoading: true });

      try {
        await axios.request(APIConfig.Portal.createSupplierGroupCompany(portalId, companyName, { groupId, person }));
        executeRefetchPortalSupplierGroups();
      } catch {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage('Error creating new supplier group company')
        });
        dispatch({ type: PORTAL_SET_SUPPLIER_GROUPS_LOADING, isLoading: false });
      }
    },
    [eventBusClient, portalId, executeRefetchPortalSupplierGroups]
  );

  const createPortalSupplierGroupPerson = React.useCallback(
    async (
      companyKey: number,
      person: CreateSupplierGroupCompanyPerson,
      opts: CreateSupplierGroupCompanyPersonOptions = {}
    ) => {
      const { groupId } = opts;

      if (!portalId) {
        return;
      }

      dispatch({ type: PORTAL_SET_SUPPLIER_GROUPS_LOADING, isLoading: true });

      try {
        await axios.request(
          APIConfig.Portal.createSupplierGroupPerson(portalId, companyKey, person, groupId ? { groupId } : undefined)
        );
        executeRefetchPortalSupplierGroups();
      } catch {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage('Error adding supplier group')
        });
        dispatch({ type: PORTAL_SET_SUPPLIER_GROUPS_LOADING, isLoading: false });
      }
    },
    [eventBusClient, portalId, executeRefetchPortalSupplierGroups]
  );

  const createSourcingBidTeam = React.useCallback(
    async (companyKey: number, opts: CreateBidTeamOptions = {}, companyKeys?: number[]) => {
      const {
        afterCreate = noop,
        isDuplicateSelection = false,
        personCompositeKey,
        radioPerson,
        referredBy,
        shouldInvitePerson = false
      } = opts;

      const formData = new FormData();

      if (companyKey === -1 && companyKeys?.length) {
        companyKeys.forEach(item => {
          formData.append('company', `${item}`);
        });
      } else {
        formData.append('company', `${companyKey}`);
      }

      formData.append('okey', `${offeringKey}`);

      if (personCompositeKey) {
        formData.append('person', personCompositeKey);
      }

      if (isDuplicateSelection) {
        formData.append('dupselect', 'yes');
      }

      if (radioPerson) {
        formData.append('radioPerson', radioPerson);
      }

      if (referredBy) {
        formData.append('referredby', referredBy);
      }

      if (shouldInvitePerson) {
        formData.append('invitePerson', 'true');
      }

      try {
        await axios.request(APIConfig.Sourcing.createBidTeam(formData));
        afterCreate();
        await executeRefetchSourcingBidTeams();
      } catch {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage('Error adding bid team')
        });
      }
    },
    [eventBusClient, executeRefetchSourcingBidTeams, offeringKey]
  );

  const createSourcingBidTeamMember = React.useCallback(
    async (companyKey: number, opts: CreateBidTeamMemberOptions = {}, companyKeys?: number[]) => {
      const { afterCreate = noop, personCompositeKey, leadCompanyKey } = opts;

      const formData = new FormData();

      formData.append('company', `${companyKey}`);
      formData.append('okey', `${offeringKey}`);

      if (leadCompanyKey) {
        formData.append('leadCompanyKey', `${leadCompanyKey}`);
      }

      if (personCompositeKey) {
        formData.append('person', personCompositeKey);
      }

      try {
        await axios.request(APIConfig.Sourcing.createBidTeamMember(formData));
        afterCreate();
        await executeRefetchSourcingBidTeams();
      } catch {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage('Error adding user to bid team')
        });
      }
    },
    [eventBusClient, executeRefetchSourcingBidTeams, offeringKey]
  );

  const refetchPortalSupplierGroups = React.useCallback(async () => {
    executeRefetchPortalSupplierGroups();
  }, [executeRefetchPortalSupplierGroups]);

  const refetchSourcingBidTeams = React.useCallback(async () => {
    await executeRefetchSourcingBidTeams();
  }, [executeRefetchSourcingBidTeams]);

  const refetchSupplierRequests = React.useCallback(async () => {
    if (!offeringKey) {
      return;
    }

    try {
      const supplierRequestsByCompany = await loadSupplierRequestsByCompany(offeringKey);

      dispatch({
        type: SOURCING_REFETCH_SUPPLIER_REQUESTS,
        supplierRequestPersonCount: getPersonCountForSupplierRequestsByCompany(supplierRequestsByCompany),
        supplierRequests: supplierRequestsByCompany
      });
    } catch (error) {
      eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
        message: createRequestErrorMessage(
          'Error while fetching requests to join this event',
          axios.isAxiosError(error) ? error.response : undefined
        )
      });
    }
  }, [eventBusClient, offeringKey]);

  const removePortalSupplierGroup = React.useCallback(
    async (supplierGroup: SupplierGroupType) => {
      if (!portalId) {
        return;
      }

      dispatch({ type: PORTAL_SET_SUPPLIER_GROUPS_LOADING, isLoading: true });

      try {
        await axios.request(APIConfig.Portal.removeSupplierGroup(portalId, supplierGroup.id));
        enqueueSnackbar(`${supplierGroup.name} has been removed from this portal.`, { variant: 'success' });
        executeRefetchPortalSupplierGroups();
      } catch (error) {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            `Error removing supplier group "${supplierGroup.name}"`,
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
        dispatch({ type: PORTAL_SET_SUPPLIER_GROUPS_LOADING, isLoading: false });
      }
    },
    [enqueueSnackbar, executeRefetchPortalSupplierGroups, eventBusClient, portalId]
  );

  const removePortalSupplierCompanyFromSupplierGroup = React.useCallback(
    async (supplierGroup: SupplierGroupType, supplierCompany: SupplierGroupCompanyType) => {
      if (!portalId) {
        return;
      }

      const supplierGroupCompanyIds = Array.isArray(supplierCompany.supplierGroupCompanyIds)
        ? supplierCompany.supplierGroupCompanyIds
        : [];

      dispatch({ type: PORTAL_SET_SUPPLIER_GROUPS_LOADING, isLoading: true });

      try {
        const removeRequestConfigs = supplierGroupCompanyIds.map((companyId: number) =>
          APIConfig.Portal.removeSupplierCompanyFromPortal(portalId, companyId)
        );

        // can't run in parallel otherwise: if deleting more than one, each request may think the other
        // still exists and we end up with an empty supplier group. If this is ever changed in the back
        // end, we can use the following Promise.all instead
        // await Promise.all(removeRequestConfigs.map(requestConfig => axios.request(requestConfig)));
        for (const requestConfig of removeRequestConfigs) {
          await axios.request(requestConfig);
        }

        enqueueSnackbar(`${supplierCompany.name} has been removed from supplier group ${supplierGroup.name}.`, {
          variant: 'success'
        });
        refetchPortalSupplierGroups();
      } catch (error) {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            `Error removing ${supplierCompany.name} from supplier group ${supplierGroup.name}.`,
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
        dispatch({ type: PORTAL_SET_SUPPLIER_GROUPS_LOADING, isLoading: false });
      }
    },
    [enqueueSnackbar, eventBusClient, portalId, refetchPortalSupplierGroups /*, state.portal.supplierVendorMappings*/]
  );

  const removePortalSupplierCompanyPersonFromSupplierGroup = React.useCallback(
    async (supplierGroup: SupplierGroupType, person: SupplierGroupCompanyPersonType) => {
      if (!portalId) {
        return;
      }

      dispatch({ type: PORTAL_SET_SUPPLIER_GROUPS_LOADING, isLoading: true });

      try {
        await axios.request(APIConfig.Portal.removeSupplierCompanyPersonFromPortal(portalId, person.id));

        enqueueSnackbar(`${person.name} has been removed from supplier group ${supplierGroup.name}.`, {
          variant: 'success'
        });
        refetchPortalSupplierGroups();
      } catch (error) {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            `Error removing ${person.name} from supplier group ${supplierGroup.name}`,
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
        dispatch({ type: PORTAL_SET_SUPPLIER_GROUPS_LOADING, isLoading: false });
      }
    },
    [enqueueSnackbar, eventBusClient, portalId, refetchPortalSupplierGroups]
  );

  /**
   * @function
   * @param {any} bidTeam - bid team object
   * @returns void
   */
  const removeSourcingBidTeam = React.useCallback(
    async (bidTeam: any) => {
      if (!offeringKey) {
        return;
      }

      eventBusClient.sendEvent(EVENT.SHOW_BID_TEAM_PANEL_LOADING_MASK, true);

      const { altBidSeqNo, leadCompanyKey } = bidTeam.compositeKey;
      try {
        await axios.request(APIConfig.Sourcing.removeBidTeam(offeringKey, leadCompanyKey, altBidSeqNo));
        enqueueSnackbar(`${bidTeam.name} has been removed from this bid event.`, { variant: 'success' });
        await executeRefetchSourcingBidTeams();
      } catch (error) {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            `Error removing bid team "${bidTeam.name}"`,
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
        eventBusClient.sendEvent(EVENT.SHOW_BID_TEAM_PANEL_LOADING_MASK, false);
      }
    },
    [enqueueSnackbar, eventBusClient, executeRefetchSourcingBidTeams, offeringKey]
  );

  const removeSourcingSupplierCompanyFromBidTeam = React.useCallback(
    async (bidTeamSupplier: any, bidTeam: any) => {
      if (!offeringKey) {
        return;
      }

      const { leadCompanyKey, altBidSeqNo } = bidTeam.compositeKey;
      const companyKeys = getRelatedCompanyKeysForSupplierIdOnBidTeam(
        bidTeamSupplier.id,
        bidTeam,
        state.sourcing.supplierVendorMappings
      );

      eventBusClient.sendEvent(EVENT.SHOW_BID_TEAM_PANEL_LOADING_MASK, true);

      try {
        const removeRequestConfigs = companyKeys.map(companyKey =>
          APIConfig.Sourcing.removeSupplierCompanyFromBidTeam(offeringKey, companyKey, leadCompanyKey, altBidSeqNo)
        );

        await Promise.all(removeRequestConfigs.map(requestConfig => axios.request(requestConfig)));

        enqueueSnackbar(`${bidTeamSupplier.name} has been removed from bid team ${bidTeam.name}.`, {
          variant: 'success'
        });
        await executeRefetchSourcingBidTeams();
      } catch (error) {
        eventBusClient.sendEvent(EVENT.SHOW_BID_TEAM_PANEL_LOADING_MASK, false);
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            `Error removing ${bidTeamSupplier.name} from bid team ${bidTeam.name}.`,
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
      }
    },
    [
      enqueueSnackbar,
      eventBusClient,
      executeRefetchSourcingBidTeams,
      offeringKey,
      state.sourcing.supplierVendorMappings
    ]
  );

  /**
   * @function
   * @param {any} bidTeam - bid team object
   * @param {any} person - bid team person object
   * @returns void
   */
  const removeSourcingSupplierCompanyPersonFromBidTeam = React.useCallback(
    async (bidTeam: any, person: any) => {
      if (!offeringKey) {
        return;
      }

      eventBusClient.sendEvent(EVENT.SHOW_BID_TEAM_PANEL_LOADING_MASK, true);

      try {
        await axios.request(APIConfig.Sourcing.removeSupplierCompanyPersonFromBidTeam(offeringKey, person.personKey));

        enqueueSnackbar(`${person.name} has been removed from bid team ${bidTeam.name}.`, { variant: 'success' });
        await executeRefetchSourcingBidTeams();
      } catch (error) {
        eventBusClient.sendEvent(EVENT.SHOW_BID_TEAM_PANEL_LOADING_MASK, false);
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            `Error removing ${person.name} from bid team ${bidTeam.name}`,
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
      }
    },
    [enqueueSnackbar, eventBusClient, executeRefetchSourcingBidTeams, offeringKey]
  );

  const removeGlobalSupplierList = React.useCallback(
    async (list: GlobalSupplierListType) => {
      try {
        await axios.request(APIConfig.SupplierList.removeGlobalSupplierList(list.listKey, productKey));
        executeRefreshGlobalSupplierLists({ hasSelectedListBeenRemoved: true });
        enqueueSnackbar(`${list.listName} has been deleted.`, { variant: 'success' });
      } catch (error) {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            'Error while removing global supplier list',
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
      }
    },
    [enqueueSnackbar, eventBusClient, executeRefreshGlobalSupplierLists, productKey]
  );

  const removeSupplierFromCurrentGlobalSupplierList = React.useCallback(
    async (companyKey: number, companyName: string) => {
      if (
        !state.discovery.currentSelectedList ||
        !Array.isArray(state.discovery.currentSelectedList.miListAssignments)
      ) {
        return;
      }

      const assignmentToRemove = state.discovery.currentSelectedList.miListAssignments.find(
        assignment => assignment.numericKey1 === companyKey
      );

      if (!assignmentToRemove) {
        return;
      }

      const newList: GlobalSupplierListType = {
        ...state.discovery.currentSelectedList,
        miListAssignments: (state.discovery.currentSelectedList?.miListAssignments || []).filter(
          assignment => assignment.listAssignmentKey !== assignmentToRemove.listAssignmentKey
        )
      };

      try {
        await axios.request(APIConfig.SupplierList.updateGlobalSupplierList(newList, productKey));

        enqueueSnackbar(`${companyName} has been removed from ${state.discovery.currentSelectedList.listName}.`, {
          variant: 'success'
        });

        executeRefreshGlobalSupplierLists();
      } catch (error) {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            'There was an error removing a supplier from this list',
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
      }
    },
    [
      enqueueSnackbar,
      eventBusClient,
      executeRefreshGlobalSupplierLists,
      state.discovery.currentSelectedList,
      productKey
    ]
  );

  const saveGlobalSupplierList = React.useCallback(
    async (list: GlobalSupplierListType, options: SaveListOptions = {}) => {
      const { isNewList = false } = options;
      try {
        // response returns new/updated list, but xml to object conversion to messes it up too much and can't
        // be used so just make sure this returns successfully
        const response = await axios.request(
          isNewList
            ? APIConfig.SupplierList.addGlobalSupplierList(list, productKey)
            : APIConfig.SupplierList.updateGlobalSupplierList(list, productKey)
        );

        const newListKey = response.data?.listKey;
        const previousListCategoryId = state.discovery.currentSelectedListCategoryId;

        const listCategoryItem = getGlobalSupplierListCategoryItem(
          isNewList ? LIST_CATEGORY_ID.MY_LISTS : previousListCategoryId
        );
        const hasCompanyChanged = state.discovery.currentSelectedCompanyKey !== list.ownerCompany.companyKey;
        const wasCompanyListCategory = previousListCategoryId === LIST_CATEGORY_ID.COMPANY_LISTS;

        if (listCategoryItem) {
          executeGlobalSupplierListsRequest(listCategoryItem, {
            afterExecute:
              Array.isArray(list.miListAssignments) && list.miListAssignments.length > 0
                ? () => executeGlobalSupplierListSuppliersRequest(newListKey, { globalSupplierList: list })
                : () => dispatch({ type: DISCOVERY_LIST_SUPPLIERS_RESET }),
            companyKey: !isNewList && wasCompanyListCategory ? list.ownerCompany.companyKey : personCompany.companyKey,
            newSelectedListKey: isNewList && newListKey ? newListKey : list.listKey,
            shouldUpdateSelectedCompanyKey: wasCompanyListCategory && hasCompanyChanged,
            shouldUpdateSelectedList: true,
            shouldUpdateSelectedListCategory: isNewList && wasCompanyListCategory
          });
        }

        enqueueSnackbar(`${list.listName} has been ${isNewList ? 'added' : 'saved'}.`, { variant: 'success' });
      } catch (error) {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            'Error while updating global supplier list',
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
      }
    },
    [
      enqueueSnackbar,
      eventBusClient,
      executeGlobalSupplierListsRequest,
      executeGlobalSupplierListSuppliersRequest,
      personCompany.companyKey,
      state.discovery.currentSelectedCompanyKey,
      state.discovery.currentSelectedListCategoryId,
      productKey
    ]
  );

  const sendSourcingBulkEmailNotifications = React.useCallback(
    async (personKeys: ReadonlyArray<number>) => {
      if (!offeringKey) {
        return;
      }

      try {
        await axios.request(APIConfig.Sourcing.sendSupplierBulkEmail(offeringKey, personKeys));

        enqueueSnackbar('Email notifications have been sent.', { variant: 'success' });
        await executeRefetchSourcingBidTeams();
      } catch (error) {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            'There was an error sending the bulk notification email',
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
      }
    },
    [enqueueSnackbar, eventBusClient, executeRefetchSourcingBidTeams, offeringKey]
  );

  /**
   * @function
   * @param {any} person - person object from bid team object
   * @returns void
   */
  const sendSourcingSupplierPersonFirstTimeNotification = React.useCallback(
    async (person: any) => {
      if (!offeringKey) {
        return;
      }

      try {
        eventBusClient.sendEvent(EVENT.SHOW_BID_TEAM_PANEL_LOADING_MASK, true);

        await axios.request(APIConfig.Sourcing.prepareSupplierCompanyPersonNotifyEmail(offeringKey, person.personKey));
        await axios.request(
          APIConfig.Sourcing.sendBidEventMail(offeringKey, EMAIL_MESSAGE_TYPE.NOTIFICATION, person.personKey, {
            firstTime: true
          })
        );

        enqueueSnackbar(`Notification has been sent to ${person.name}.`, { variant: 'success' });
        await executeRefetchSourcingBidTeams();
      } catch (error) {
        eventBusClient.sendEvent(EVENT.SHOW_BID_TEAM_PANEL_LOADING_MASK, false);
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            `There was an error sending a notification to ${person.name}`,
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
      }
    },
    [enqueueSnackbar, eventBusClient, executeRefetchSourcingBidTeams, offeringKey]
  );

  const sendSourcingSupplierCompanyPersonNotification = React.useCallback(
    async (person: any, opts: SendSupplierCompanyPersonNotificationOptions = {}) => {
      const { updatedEmailAddress } = opts;

      if (!offeringKey) {
        return;
      }

      try {
        const isActivePerson = person.personStatusCode === PERSON_STATUS_CODE.ACTIVE;
        eventBusClient.sendEvent(EVENT.SHOW_BID_TEAM_PANEL_LOADING_MASK, true);

        // send preparation request
        await axios.request(
          isActivePerson
            ? APIConfig.Sourcing.prepareSupplierCompanyPersonReNotifyEmail(
                offeringKey,
                person.personKey,
                updatedEmailAddress || person.email
              )
            : APIConfig.Sourcing.prepareSupplierCompanyPersonReInviteEmail(
                offeringKey,
                person.personKey,
                updatedEmailAddress || person.email
              )
        );
        // send email request
        await axios.request(
          APIConfig.Sourcing.sendBidEventMail(
            offeringKey,
            isActivePerson ? EMAIL_MESSAGE_TYPE.NOTIFICATION : EMAIL_MESSAGE_TYPE.INVITATION,
            person.personKey,
            updatedEmailAddress ? { newAddress: updatedEmailAddress } : undefined
          )
        );

        enqueueSnackbar(`${isActivePerson ? 'Notification' : 'Invitation'} has been sent to ${person.name}.`, {
          variant: 'success'
        });
        await executeRefetchSourcingBidTeams();
      } catch (error) {
        eventBusClient.sendEvent(EVENT.SHOW_BID_TEAM_PANEL_LOADING_MASK, false);
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            `There was an error sending an email to ${person.name}`,
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
      }
    },
    [enqueueSnackbar, eventBusClient, executeRefetchSourcingBidTeams, offeringKey]
  );

  const sendSourcingSupplierInvite = React.useCallback(
    async (formData: FormData) => {
      if (!offeringKey) {
        return;
      }

      try {
        formData.append('okey', `${offeringKey}`);

        // send email request
        await axios.request(APIConfig.Sourcing.inviteSupplier(formData));
        await executeRefetchSourcingBidTeams();
      } catch (error) {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            `There was an error creating a new person or company}`,
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
      }
    },
    [eventBusClient, executeRefetchSourcingBidTeams, offeringKey]
  );

  const createNewCompanyOnly = React.useCallback(
    async (formData: FormData): Promise<{ companyKey: number }> => {
      try {
        dispatch({ type: DISCOVERY_LIST_SUPPLIERS_SET_LOADING, isLoading: true });
        const { data } = await axios.request(APIConfig.Sourcing.createNewCompany(formData));
        eventBusClient.sendEvent(EVENT.SHOW_BID_TEAM_PANEL_LOADING_MASK, false);
        const companyKey = data?.companyKey;
        return { companyKey: companyKey ?? 0 };
      } catch (err) {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            `There was an error creating a new company}`,
            axios.isAxiosError(err) ? err.response : undefined
          )
        });
        return { companyKey: 0 };
      } finally {
        dispatch({ type: DISCOVERY_LIST_SUPPLIERS_SET_LOADING, isLoading: false });
      }
    },
    [eventBusClient]
  );

  const updateDiscoverySelectedList = React.useCallback(
    async (listKey: number, options: UpdateDiscoverySelectedListOptions = {}) => {
      const { afterUpdate = noop } = options;

      const list = state.discovery.currentLists.find(list => list.listKey === listKey);

      if (list) {
        dispatch({ type: DISCOVERY_SELECTED_LIST_UPDATE, list });
        executeGlobalSupplierListSuppliersRequest(listKey, { afterExecute: afterUpdate });
      }
    },
    [executeGlobalSupplierListSuppliersRequest, state.discovery.currentLists]
  );

  const updateDiscoverySelectedListCategory = React.useCallback(
    async (listCategoryId: ListCategoryIdType) => {
      const listCategoryItem = getGlobalSupplierListCategoryItem(listCategoryId);

      if (listCategoryItem) {
        dispatch({ type: DISCOVERY_CURRENT_LISTS_SET_LOADING, isLoading: true });
        dispatch({
          type: DISCOVERY_SELECTED_LIST_CATEGORY_UPDATE,
          listCategoryId,
          personCompanyKey: personCompany.companyKey
        });

        executeGlobalSupplierListsRequest(listCategoryItem, { companyKey: personCompany.companyKey });
      }
    },
    [executeGlobalSupplierListsRequest, personCompany.companyKey]
  );

  const updateDiscoverySelectedListCompany = React.useCallback(
    (companyKey: number, options: UpdateDiscoverySelectedListCompanyOptions = {}) => {
      const { afterUpdate = noop } = options;

      dispatch({ type: DISCOVERY_SELECTED_COMPANY_UPDATE, companyKey });

      const listCategoryItem = getGlobalSupplierListCategoryItem(state.discovery.currentSelectedListCategoryId);

      if (listCategoryItem) {
        executeGlobalSupplierListsRequest(listCategoryItem, { afterExecute: afterUpdate, companyKey });
      }
    },
    [executeGlobalSupplierListsRequest, state.discovery.currentSelectedListCategoryId]
  );

  const updatePortalSupplierGroup = React.useCallback(
    async (supplierGroup: SupplierGroupType, opts: UpdateSupplierTeamOptions = {}) => {
      const {
        errorMessage = 'Error while updating supplier group',
        successMessage = `${supplierGroup.name} has been updated.`
      } = opts;

      if (!portalId || !supplierGroup) {
        return;
      }

      try {
        await axios.request(APIConfig.Portal.updateSupplierGroup(portalId, supplierGroup.id, supplierGroup));
        enqueueSnackbar(successMessage, { variant: 'success' });
        executeRefetchPortalSupplierGroups();
      } catch (error) {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(errorMessage, axios.isAxiosError(error) ? error.response : undefined)
        });
      }
    },
    [enqueueSnackbar, executeRefetchPortalSupplierGroups, eventBusClient, portalId]
  );

  const updatePortalSupplierGroupMainContact = React.useCallback(
    async (supplierGroup: SupplierGroupType, person: SupplierGroupCompanyPersonType) => {
      if (!portalId) {
        return;
      }

      dispatch({ type: PORTAL_SET_SUPPLIER_GROUPS_LOADING, isLoading: true });

      try {
        await axios.request(APIConfig.Portal.updateSupplierGroupMainContact(portalId, person.id));
        enqueueSnackbar(`${supplierGroup.name} main contact changed to ${person.name}.`, { variant: 'success' });
        executeRefetchPortalSupplierGroups();
      } catch (error) {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            'Error while updating main contact for supplier group',
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
        dispatch({ type: PORTAL_SET_SUPPLIER_GROUPS_LOADING, isLoading: false });
      }
    },
    [enqueueSnackbar, eventBusClient, executeRefetchPortalSupplierGroups, portalId]
  );

  const updatePortalSupplierGroupName = React.useCallback(
    (supplierGroup: SupplierGroupType, newSupplierGroupName: string) => {
      updatePortalSupplierGroup(
        { ...supplierGroup, name: newSupplierGroupName },
        {
          errorMessage: 'Error while updating supplier group name',
          successMessage: `Supplier group name has been changed from ${supplierGroup.name} to ${newSupplierGroupName}.`
        }
      );
    },
    [updatePortalSupplierGroup]
  );

  const updateSourcingBidTeamApprovalStatus = React.useCallback(
    async (bidTeam: any, approvalStatus: string) => {
      if (!offeringKey) {
        return;
      }

      const { leadCompanyKey, altBidSeqNo } = bidTeam.compositeKey;

      try {
        await axios.request(
          APIConfig.Sourcing.updateBidTeamApprovalStatus(offeringKey, approvalStatus, leadCompanyKey, altBidSeqNo)
        );

        enqueueSnackbar(`${bidTeam.name} approval status updated.`, { variant: 'success' });
        await executeRefetchSourcingBidTeams();
      } catch (error) {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            'Error while setting the Approval Status',
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
      }
    },
    [enqueueSnackbar, eventBusClient, executeRefetchSourcingBidTeams, offeringKey]
  );

  const updateSourcingBidTeamBidIntention = React.useCallback(
    async (bidTeam: any, bidIntention: string) => {
      if (!offeringKey) {
        return;
      }

      const { leadCompanyKey, altBidSeqNo } = bidTeam.compositeKey;

      try {
        await axios.request(
          APIConfig.Sourcing.updateBidTeamBidIntention(offeringKey, bidIntention, leadCompanyKey, altBidSeqNo)
        );
        enqueueSnackbar(`${bidTeam.name} bid intention updated.`, { variant: 'success' });
        await executeRefetchSourcingBidTeams();
      } catch (error) {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            'Error while setting the Bid Intention',
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
      }
    },
    [enqueueSnackbar, eventBusClient, executeRefetchSourcingBidTeams, offeringKey]
  );

  const updateSourcingBidTeam = React.useCallback(
    async (bidTeam: any, opts: UpdateSupplierTeamOptions = {}) => {
      const { errorMessage = 'Error while updating bid team', successMessage = `${bidTeam.name} has been updated.` } =
        opts;

      if (!bidTeam) {
        return;
      }

      try {
        await axios.request(APIConfig.Sourcing.updateBidTeam(bidTeam));
        enqueueSnackbar(successMessage, { variant: 'success' });
        await executeRefetchSourcingBidTeams();
      } catch (error) {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(errorMessage, axios.isAxiosError(error) ? error.response : undefined)
        });
      }
    },
    [enqueueSnackbar, eventBusClient, executeRefetchSourcingBidTeams]
  );

  const updateSourcingBidTeamMainContact = React.useCallback(
    (bidTeam: any, person: any) => {
      updateSourcingBidTeam(
        { ...bidTeam, mainContactPersonKey: person.personKey },
        {
          errorMessage: 'Error while updating main contact for bid team',
          successMessage: `${bidTeam.name} main contact changed to ${person.name}.`
        }
      );
    },
    [updateSourcingBidTeam]
  );

  const updateSourcingBidTeamName = React.useCallback(
    (bidTeam: any, bidTeamName: string) => {
      updateSourcingBidTeam(
        { ...bidTeam, name: bidTeamName },
        {
          errorMessage: 'Error while updating bid team name',
          successMessage: `Bid team name has been changed from ${bidTeam.name} to ${bidTeamName}.`
        }
      );
    },
    [updateSourcingBidTeam]
  );

  //
  // --- Hook Return ---

  const contextValue: ProductContextValue = [
    // first element, product context data
    state,
    // second element, product context actions
    {
      discoveryAction: {
        addSupplierToCurrentGlobalSupplierList,
        removeList: removeGlobalSupplierList,
        removeSupplierFromCurrentGlobalSupplierList,
        saveList: saveGlobalSupplierList,
        updateSelectedList: updateDiscoverySelectedList,
        updateSelectedListCategory: updateDiscoverySelectedListCategory,
        updateSelectedListCompany: updateDiscoverySelectedListCompany
      },
      portalAction: {
        addSupplierGroupCompany: addPortalSupplierGroupCompany,
        addSupplierGroupPerson: addPortalSupplierGroupPerson,
        createSupplierGroup: createPortalSupplierGroup,
        createSupplierGroupCompany: createPortalSupplierGroupCompany,
        createSupplierGroupPerson: createPortalSupplierGroupPerson,
        refetchSupplierGroups: refetchPortalSupplierGroups,
        removeSupplierCompanyFromSupplierGroup: removePortalSupplierCompanyFromSupplierGroup,
        removeSupplierCompanyPersonFromSupplierGroup: removePortalSupplierCompanyPersonFromSupplierGroup,
        removeSupplierGroup: removePortalSupplierGroup,
        updateSupplierGroupMainContact: updatePortalSupplierGroupMainContact,
        updateSupplierGroupName: updatePortalSupplierGroupName
      },
      sourcingAction: {
        createBidTeam: createSourcingBidTeam,
        createBidTeamMember: createSourcingBidTeamMember,
        refetchBidTeams: refetchSourcingBidTeams,
        removeBidTeam: removeSourcingBidTeam,
        removeSupplierCompanyFromBidTeam: removeSourcingSupplierCompanyFromBidTeam,
        removeSupplierCompanyPersonFromBidTeam: removeSourcingSupplierCompanyPersonFromBidTeam,
        sendBulkEmailNotifications: sendSourcingBulkEmailNotifications,
        sendSupplierCompanyPersonFirstTimeNotification: sendSourcingSupplierPersonFirstTimeNotification,
        sendSupplierCompanyPersonNotification: sendSourcingSupplierCompanyPersonNotification,
        sendSupplierInvite: sendSourcingSupplierInvite,
        updateBidTeam: updateSourcingBidTeam,
        updateBidTeamApprovalStatus: updateSourcingBidTeamApprovalStatus,
        updateBidTeamBidIntention: updateSourcingBidTeamBidIntention,
        updateBidTeamMainContact: updateSourcingBidTeamMainContact,
        updateBidTeamName: updateSourcingBidTeamName,
        createCompanyOnly: createNewCompanyOnly
      }
    }
  ];

  return <ProductContext.Provider value={contextValue}>{children}</ProductContext.Provider>;
};

//
// --- useProductContextHook ---

/**
 * useProductContext hook
 *
 * external hook that returns product data and action handlers based on the current product
 * type app is being used on.
 *
 * The return result is an array where the the first element is a data object. The second element
 * is a map of actions.
 *
 * Example Use 1:
 *   const [productData, productAction] = useProductContext();
 *
 * Example Use 2:
 *   const [{ productType, sourcing }, { sourcingAction }] = useProductContext();
 *
 * Example Use 3:
 *   const [{ productType, sourcing }, { sourcingAction: { refreshBidTeams } }] = useProductContext();
 *
 * @returns {[ProductData, ProductAction]}
 */
export const useProductContext = (): ProductContextValue => {
  const context = React.useContext(ProductContext);

  if (!context) {
    throw new Error('useProductContext must be used within a ProductContextProvider');
  }

  return context;
};
